/* eslint-disable max-lines */
import React, { useContext, useState } from 'react';
import {
  Col,
  FormGroup,
  Input,
  Label,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
  Badge,
  Button,
} from 'reactstrap';
import { Context as AuthContext } from '../../../../contexts/AuthContext';
import CardInfo from '../../../../components/Cards/CardInfo';
import CountSkeleton from './CountSkeleton';
import ModalContent from '../../../../components/Modals/Modal';
import EmployeeService from '../../../../services/Employee';
import EmployeeTerminate from '../../../../services/EmployeeTerminate';
import confirmAlert from '../../../../helpers/confirmAlert';
import { getNewHireColumns } from './ColumnsNewHire';
import DataTableWithoutHeaderAndPagination from '../../../../components/Core/DataTableWithoutHeaderAndPagination';
import TruncatedCell from '../../../../components/Core/TruncatedCell';
import NewHireReviewModal from './NewHireReviewModal';
import FinalizeTerminationModal from './FinalizeTerminationModal';
import ViewAndPrintTerminationModal from './ViewAndPrintTerminationModal';
import ApprovalChatModal from './ApprovalChatModal';
import { toast } from 'react-toastify';
import { PayRatesList } from '../../../../components/Employee';
import confirmAlertWithForm from '../../../../helpers/confirmAlertWithForm';
import isHRUser from '../../../../helpers/isHRUser';
import isFinanceUser from '../../../../helpers/isFinanceUser';
import isAdmin from '../../../../helpers/isAdmin';
import isDirector from '../../../../helpers/isDirector';
import isUpperManagement from '../../../../helpers/isUpperManagement';
import isEducationDirectorAdminAssistant from '../../../../helpers/isEducationDirectorAdminAssistant';
import { toLocaleFormatSlash } from '../../../../helpers/dateFormat';
import moment from 'moment';
import { ShowModalModes } from './index';

const canSeeTermination = (user: any) =>
  isAdmin(user) ||
  isHRUser(user) ||
  isDirector(user) ||
  isUpperManagement(user) ||
  isEducationDirectorAdminAssistant(user);

const canApproveOrDenyTermination = (user: any) =>
  isAdmin(user) || isHRUser(user) || isUpperManagement(user) || isEducationDirectorAdminAssistant(user);

const canFinalizeTermination = (user: any) =>
  isAdmin(user) || isDirector(user) || isUpperManagement(user) || isHRUser(user);

export const getApprovalsVisibleCount = (user: any): number => {
  let count = 0;
  if (!isFinanceUser(user)) count += 1;
  if (!isHRUser(user)) count += 1;
  if (canSeeTermination(user)) count += 1;
  return count;
};

export const getApprovalsColMd = (user: any): string => {
  const visible = getApprovalsVisibleCount(user);
  if (visible <= 1) return '2';
  if (visible === 2) return '4';
  return '5';
};

export const ApprovalRequests: React.FC<{
  loading: boolean;
  newHires: any[];
  loadList: () => void;
  payRasesCount: number;
  terminationsPending?: any[];
  schoolId: string;
  showModal?: ShowModalModes;
  payRatesListRefreshKey?: number;
  terminationStatusFilter?: string;
  onTerminationStatusFilterChange?: (status: string) => void;
  onModalClose?: () => void;
}> = ({
  loading,
  newHires,
  loadList,
  payRasesCount,
  terminationsPending = [],
  schoolId: _schoolId,
  showModal,
  payRatesListRefreshKey,
  terminationStatusFilter = '',
  onTerminationStatusFilterChange,
  onModalClose,
}) => {
  const { user, token }: any = useContext(AuthContext);

  const [newHireReviewShow, setNewHireReviewShow] = useState<boolean>(false);
  const [selectedNewHire, setSelectedNewHire] = useState<any>({});
  const [approveTerminationShow, setApproveTerminationShow] = useState<boolean>(false);
  const [selectedTerminationId, setSelectedTerminationId] = useState<string | null>(null);
  const [viewPrintShow, setViewPrintShow] = useState<boolean>(false);
  const [viewPrintRow, setViewPrintRow] = useState<any>(null);
  const [chatOpen, setChatOpen] = useState<boolean>(false);
  const [chatApprovalType, setChatApprovalType] = useState<string>('');
  const [chatApprovalId, setChatApprovalId] = useState<string>('');
  const [chatTitle, setChatTitle] = useState<string>('');

  const openChat = (approvalType: string, approvalId: string, title: string) => {
    setChatApprovalType(approvalType);
    setChatApprovalId(approvalId);
    setChatTitle(title);
    setChatOpen(true);
  };
  const hiresCount = newHires.length || 0;

  const countPayRaises = payRasesCount || 0;

  const handleApproveOrDeny = async (e: React.FormEvent, action: string, id: string) => {
    if (action === 'approve' && id) {
      e.preventDefault();
      return confirmAlert({
        title: 'Confirm',
        messageType: 'EDIT',
        message: `Are you sure you want to ${action}?`,
        id,
        onClickYes: () => approveOrDeny(action, id),
      });
    }
    if (action === 'deny' && id) {
      return confirmAlertWithForm({
        title: 'Deny',
        messageType: 'DELETE',
        message: `Are you sure you want to ${action}?`,
        children: (
          <Row className="mt-4">
            <Col>
              <FormGroup className="form-control-label">
                <Label>Reason</Label>
                <Input type="textarea" name="reason" />
              </FormGroup>
            </Col>
          </Row>
        ),
        onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
          e?.preventDefault();
          const data = new FormData(e.target as HTMLFormElement);
          const body = {} as any;
          for (const [key, value] of data.entries()) {
            (body as any)[key] = value;
          }
          approveOrDeny(action, id, body);
        },
      });
    }
  };

  const approveOrDeny = async (action: string, id: string, body = {}) => {
    try {
      await EmployeeService(token).adminApproveOrDeny(action, id, body);
      toast.success('Saved successfully!');
      loadList();
    } catch (e: any) {
      console.error(e);
    }
  };

  const handleTerminationApproveOrDeny = (e: React.FormEvent, action: string, id: string) => {
    if (action === 'approve' && id) {
      e.preventDefault();
      return confirmAlert({
        title: 'Confirm',
        messageType: 'EDIT',
        message: 'Are you sure you want to approve?',
        id,
        onClickYes: () => terminationApproveOrDeny(action, id),
      });
    }
    if (action === 'deny' && id) {
      return confirmAlertWithForm({
        title: 'Deny',
        messageType: 'DELETE',
        message: 'Are you sure you want to deny?',
        children: (
          <Row className="mt-4">
            <Col>
              <FormGroup className="form-control-label">
                <Label>Reason</Label>
                <Input type="textarea" name="reason" />
              </FormGroup>
            </Col>
          </Row>
        ),
        onSubmit: (e: React.FormEvent<HTMLFormElement>) => {
          e?.preventDefault();
          const formData = new FormData(e.target as HTMLFormElement);
          const reason = (formData.get('reason') as string) || '';
          terminationApproveOrDeny(action, id, reason);
        },
      });
    }
  };

  const terminationApproveOrDeny = async (action: string, id: string, reason = '') => {
    try {
      if (action === 'approve') {
        await EmployeeTerminate(token).approve(id);
        toast.success('Termination approved.');
        loadList();
        onModalClose?.();
      } else if (action === 'deny') {
        await EmployeeTerminate(token).deny(id, reason);
        toast.success('Termination denied.');
        loadList();
        onModalClose?.();
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Request error');
    }
  };

  const handleApproveTerminationSuccess = () => {
    loadList();
    onModalClose?.();
  };

  const handleViewPrint = (row: any) => {
    setViewPrintRow(row);
    setViewPrintShow(true);
  };

  const handleHideRow = async (id: string) => {
    try {
      await EmployeeTerminate(token).hide(id);
      toast.success('Row hidden. It will no longer appear in the list for any user.');
      loadList();
      onModalClose?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to hide row.');
    }
  };

  const handleHideRowClick = (id: string) => {
    return confirmAlert({
      title: 'Confirm',
      messageType: 'DELETE',
      message: 'Are you sure you want to hide this row?',
      id,
      onClickYes: () => handleHideRow(id),
    });
  };

  const handleRevokeTermination = async (employeeId: string) => {
    if (!employeeId) {
      toast.error('Could not identify the employee for this termination.');
      return;
    }
    try {
      await EmployeeTerminate(token).revoke(employeeId);
      toast.success('Termination deleted. The employee is active again.');
      loadList();
      onModalClose?.();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to delete termination.');
    }
  };

  const handleRevokeTerminationClick = (row: any) => {
    const employeeId = row?.employee?.id;
    if (!employeeId) {
      toast.error('Could not identify the employee for this termination.');
      return;
    }
    const name =
      `${row?.employee?.user?.firstName || ''} ${row?.employee?.user?.lastName || ''}`.trim() || 'this employee';
    return confirmAlert({
      title: 'Delete termination',
      messageType: 'DELETE',
      message:
        `Delete this termination? ${name} will be set back to Active and their termination date removed, ` +
        'returning them to the system. This can affect their final paycheck, benefits, PTO accruals and ' +
        'payroll — update those if applicable.',
      id: employeeId,
      onClickYes: (id: string) => handleRevokeTermination(id),
    });
  };

  const terminationColumns = [
    {
      name: 'Date',
      selector: (row: any) => row.createdAt,
      sortable: true,
      cell: (row: any) => (row.createdAt ? moment(row.createdAt).format('MM/DD/YYYY') : '-'),
    },
    {
      name: 'Employee',
      selector: (row: any) => `${row?.employee?.user?.firstName || ''} ${row?.employee?.user?.lastName || ''}`.trim(),
      sortable: true,
      cell: (row: any) =>
        `${row?.employee?.user?.firstName || ''} ${row?.employee?.user?.lastName || ''}`.trim() || '-',
    },
    {
      name: 'Last Work Day',
      selector: (row: any) => row.lastWorkDay,
      sortable: true,
      cell: (row: any) => (row.lastWorkDay ? toLocaleFormatSlash(row.lastWorkDay) : '-'),
    },
    {
      name: 'Reason',
      selector: (row: any) => row.reason,
      sortable: true,
      cell: (row: any) => <TruncatedCell text={row.reason} maxLength={40} />,
    },
    {
      name: 'Status',
      selector: (row: any) =>
        row.deniedAt
          ? 'denied'
          : row.approvedAt && row.signedDocumentKey
          ? 'finished'
          : row.approvedAt
          ? 'approved'
          : 'waiting',
      sortable: true,
      cell: (row: any) => {
        const isDenied = !!row.deniedAt;
        const isFinished = !!row.approvedAt && !!row.signedDocumentKey;
        const isApproved = !!row.approvedAt && !row.signedDocumentKey;
        const label = isFinished ? 'Finished' : isApproved ? 'Approved' : isDenied ? 'Denied' : 'Waiting';
        if (isFinished) {
          return (
            <Badge color="success" style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}>
              {label}
            </Badge>
          );
        }
        if (isApproved) {
          return (
            <Badge color="info" style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}>
              {label}
            </Badge>
          );
        }
        if (isDenied) {
          return (
            <Badge color="danger" style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}>
              {label}
            </Badge>
          );
        }
        return (
          <Badge
            color=""
            className="badge-dot badge-lg"
            style={{ fontSize: '0.8125rem', verticalAlign: 'middle', lineHeight: 'normal' }}
          >
            <i className="bg-purple mr-2" style={{ verticalAlign: 'baseline' }} />
            {label}
          </Badge>
        );
      },
    },
    {
      name: 'Actions',
      cell: (row: any) => {
        const isWaiting = !row.approvedAt && !row.deniedAt;
        const isFinishedRow = !!row.approvedAt && !!row.signedDocumentKey;
        const isApprovedNotFinalized =
          row.approvedAt &&
          !row.signedDocumentKey &&
          (row.submittedByUserId === user?.id || canFinalizeTermination(user));
        const canShowApproveDeny = isWaiting && canApproveOrDenyTermination(user);
        const canDeleteApproved = !!row.approvedAt && !row.signedDocumentKey && canFinalizeTermination(user);
        const terminationName = `${row?.employee?.user?.firstName || ''} ${row?.employee?.user?.lastName || ''}`.trim();
        return (
          <div className="d-flex justify-content-center align-items-center gap-1">
            <Button
              outline
              color="primary"
              size="sm"
              style={{ padding: '0.1rem 0.4rem', border: 'none' }}
              title="Termination Details"
              onClick={() => handleViewPrint(row)}
            >
              <i className="fas fa-eye" />
            </Button>
            <Button
              outline
              color="primary"
              size="sm"
              style={{ padding: '0.1rem 0.4rem', border: 'none' }}
              title="Chat"
              onClick={() => openChat('termination', row?.id, `Chat - Termination: ${terminationName}`)}
            >
              <i className="fas fa-comment" />
            </Button>
            {canShowApproveDeny && (
              <>
                <Button
                  outline
                  color="success"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Approve"
                  onClick={(e: any) => handleTerminationApproveOrDeny(e, 'approve', row?.id)}
                >
                  <i className="fas fa-check" />
                </Button>
                <Button
                  outline
                  color="danger"
                  size="sm"
                  style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                  title="Deny"
                  onClick={(e: any) => handleTerminationApproveOrDeny(e, 'deny', row?.id)}
                >
                  <i className="fas fa-times" />
                </Button>
              </>
            )}
            {isApprovedNotFinalized && (
              <Button
                outline
                color="info"
                size="sm"
                style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                title="Finalize termination"
                onClick={() => {
                  setSelectedTerminationId(row?.id);
                  setApproveTerminationShow(true);
                }}
              >
                <i className="fas fa-file-signature" />
              </Button>
            )}
            {canDeleteApproved && (
              <Button
                outline
                color="danger"
                size="sm"
                style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                title="Delete termination (employee stays active)"
                onClick={() => handleRevokeTerminationClick(row)}
              >
                <i className="fas fa-trash" />
              </Button>
            )}
            {!isWaiting && !isApprovedNotFinalized && !isFinishedRow && (
              <Button
                outline
                color="warning"
                size="sm"
                style={{ padding: '0.1rem 0.4rem', border: 'none' }}
                title="Hide row from list"
                onClick={() => handleHideRowClick(row?.id)}
              >
                <i className="fas fa-trash" />
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const newHireColumns = getNewHireColumns(
    setSelectedNewHire,
    setNewHireReviewShow,
    handleApproveOrDeny,
    user,
    (id: string) => {
      const hire = newHires.find((h: any) => h.id === id);
      const name = hire ? `${hire?.user?.firstName || ''} ${hire?.user?.lastName || ''}`.trim() : '';
      openChat('new_hire', id, `Chat - New Hire: ${name}`);
    },
  );

  const [toggleModal, setToggleModal] = useState(!!showModal);

  const terminationsCount = (terminationsPending || []).filter(
    (row: any) => !(row?.approvedAt && row?.signedDocumentKey),
  ).length;

  const computeDefaultTab = (): string => {
    if (showModal) return showModal.toString();
    const candidates = [
      { tab: '1', visible: !isFinanceUser(user), count: hiresCount },
      { tab: '2', visible: !isHRUser(user), count: countPayRaises },
      { tab: '3', visible: canSeeTermination(user), count: terminationsCount },
    ];
    const firstPending = candidates.find((c) => c.visible && c.count > 0);
    if (firstPending) return firstPending.tab;
    return isFinanceUser(user) ? '2' : canSeeTermination(user) ? '3' : '1';
  };

  const [activeTab, setActiveTab] = useState<string>(() => computeDefaultTab());

  const customStyles = {
    navLink: {
      minHeight: '42px',
      paddingTop: '8px',
      cursor: 'pointer',
    },
  };

  const handleToggleModal = () => {
    const wasOpen = toggleModal;
    if (!wasOpen) {
      setActiveTab(computeDefaultTab());
    }
    setToggleModal(!toggleModal);
    if (wasOpen && onModalClose) {
      onModalClose();
    }
  };

  return (
    <>
      <CardInfo class="bg-primary card-info-mg-button" title="Approvals" icon="fa-clock" toggle={handleToggleModal}>
        <Row className="mt-4 justify-content-center">
          {(() => {
            const showHires = !isFinanceUser(user);
            const showPayRaises = !isHRUser(user);
            const showTermination = canSeeTermination(user);
            const visibleCount = [showHires, showPayRaises, showTermination].filter(Boolean).length;
            const colSize = visibleCount === 1 ? '12' : visibleCount === 2 ? '6' : '4';
            const dividerStyle = { borderRight: '#CCC 1px solid' };

            const items: Array<{ key: string; value: React.ReactNode; label: string }> = [];
            if (showHires) {
              items.push({ key: 'hires', value: hiresCount, label: 'New Hires' });
            }
            if (showPayRaises) {
              items.push({
                key: 'payRaises',
                value: countPayRaises < 9 ? countPayRaises : '+9',
                label: 'Pay Raises',
              });
            }
            if (showTermination) {
              items.push({
                key: 'termination',
                value: terminationsCount < 9 ? terminationsCount : '+9',
                label: 'Termination',
              });
            }

            return items.map((item, index) => (
              <Col
                key={item.key}
                md={colSize}
                xs={colSize}
                className="text-center"
                style={{ padding: 0, ...(index < items.length - 1 ? dividerStyle : {}) }}
              >
                <h2 style={{ fontSize: '25px', fontWeight: 900, color: '#525f7f' }}>
                  {loading ? <CountSkeleton /> : item.value}
                </h2>
                <p style={{ fontSize: '10px' }}>{item.label}</p>
              </Col>
            ));
          })()}
        </Row>
      </CardInfo>
      <ModalContent
        title="Approvals"
        modal={toggleModal}
        toggle={handleToggleModal}
        size="xl"
        fullscreen={true}
        class="overflow-hidden"
      >
        <div style={{ maxHeight: 'calc(100vh - 180px)', overflowY: 'auto', overflowX: 'hidden' }}>
          <Row className="mx-0">
            <Col xs="12">
              <Nav tabs>
                {!isFinanceUser(user) && (
                  <NavItem>
                    <NavLink
                      className={`${activeTab === '1' ? 'active' : ''}`}
                      onClick={() => setActiveTab('1')}
                      style={customStyles.navLink}
                    >
                      New Hires
                    </NavLink>
                  </NavItem>
                )}
                {!isHRUser(user) && (
                  <NavItem>
                    <NavLink
                      className={`${activeTab === '2' ? 'active' : ''}`}
                      onClick={() => setActiveTab('2')}
                      style={customStyles.navLink}
                    >
                      Pay Raises
                    </NavLink>
                  </NavItem>
                )}
                {canSeeTermination(user) && (
                  <NavItem>
                    <NavLink
                      className={`${activeTab === '3' ? 'active' : ''}`}
                      onClick={() => setActiveTab('3')}
                      style={customStyles.navLink}
                    >
                      Termination
                    </NavLink>
                  </NavItem>
                )}
              </Nav>
            </Col>
            <Col xs="12" style={{ minWidth: 0 }}>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  <DataTableWithoutHeaderAndPagination
                    title=""
                    columns={newHireColumns}
                    data={newHires}
                    loading={loading}
                    button={false}
                    buttonUrl=""
                    onChangeRowsPerPage={(() => {}) as any}
                    onChangePage={(() => {}) as any}
                  />
                </TabPane>
                <TabPane tabId="2">{activeTab === '2' && <PayRatesList key={payRatesListRefreshKey} />}</TabPane>
                <TabPane tabId="3">
                  {activeTab === '3' && (
                    <>
                      {onTerminationStatusFilterChange && (
                        <Row className="mb-3 mt-3">
                          <Col xs="12" md="4">
                            <FormGroup>
                              <Label for="termination-status-filter">Filter by status:</Label>
                              <Input
                                id="termination-status-filter"
                                type="select"
                                value={terminationStatusFilter}
                                onChange={(e) => onTerminationStatusFilterChange(e.target.value)}
                              >
                                <option value="">All (without the hidden rows)</option>
                                <option value="waiting">Waiting</option>
                                <option value="approved">Approved</option>
                                <option value="denied">Denied</option>
                                <option value="finished">Finished</option>
                              </Input>
                            </FormGroup>
                          </Col>
                        </Row>
                      )}
                      <DataTableWithoutHeaderAndPagination
                        title=""
                        columns={terminationColumns}
                        data={terminationsPending || []}
                        loading={loading}
                        button={false}
                        buttonUrl=""
                        onChangeRowsPerPage={(() => {}) as any}
                        onChangePage={(() => {}) as any}
                      />
                    </>
                  )}
                </TabPane>
              </TabContent>
            </Col>
          </Row>
        </div>
      </ModalContent>
      <NewHireReviewModal
        isOpen={newHireReviewShow}
        toggle={() => setNewHireReviewShow(!newHireReviewShow)}
        data={selectedNewHire}
      />
      {selectedTerminationId && (
        <FinalizeTerminationModal
          isOpen={approveTerminationShow}
          toggle={() => {
            setApproveTerminationShow(false);
            setSelectedTerminationId(null);
          }}
          terminationId={selectedTerminationId}
          onSuccess={handleApproveTerminationSuccess}
        />
      )}
      {viewPrintRow?.id && (
        <ViewAndPrintTerminationModal
          isOpen={viewPrintShow}
          toggle={() => {
            setViewPrintShow(false);
            setViewPrintRow(null);
          }}
          terminationId={viewPrintRow.id}
          row={viewPrintRow}
        />
      )}
      <ApprovalChatModal
        isOpen={chatOpen}
        toggle={() => setChatOpen(false)}
        approvalType={chatApprovalType}
        approvalId={chatApprovalId}
        title={chatTitle}
        schoolId={_schoolId}
      />
    </>
  );
};
